import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';
import moment from 'moment';
import Link from '../../components/Link';
import { Star, BarChart2, Percent, Ship, Clock, Crown, Eye, Trophy, Settings, RefreshCw } from 'lucide-react';

import s from './FeaturedDashboard.css';
import TopUpModal from '../../components/ManageListing/TopUpModal';
import AnalyticsPanel from '../../components/ManageListing/AnalyticsPanel';
import CompetitionPanel from '../../components/ManageListing/CompetitionPanel';

// GraphQL Query
const GET_FEATURED_DASHBOARD = gql`
  query getMyFeaturedListings {
    getMyFeaturedListings {
      id
      listId
      status
      monthlyFee
      commissionRate
      startDate
      endDate
      autoRenew
      tier
      position
      totalCompetitors
      listing {
        id
        title
        city
        coverPhoto
        listPhotos {
          id
          name
        }
      }
    }
  }
`;

class FeaturedDashboard extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      filter: 'all',
      loading: true,
      topUpModal: {
        isOpen: false,
        featuredListingId: null,
        listId: null,
        listTitle: '',
        currentCommissionRate: 0
      },
      analyticsModal: {
        isOpen: false,
        featuredListingId: null,
        listTitle: ''
      },
      competitionModal: {
        isOpen: false,
        featuredListingId: null,
        listTitle: ''
      }
    };
  }

  openTopUpModal = (featured) => {
    this.setState({
      topUpModal: {
        isOpen: true,
        featuredListingId: featured.id,
        listId: featured.listId,
        listTitle: featured.listing?.title || '',
        currentCommissionRate: parseFloat(featured.commissionRate) || 0
      }
    });
  };

  closeTopUpModal = () => {
    this.setState({
      topUpModal: { isOpen: false, featuredListingId: null, listId: null, listTitle: '', currentCommissionRate: 0 }
    });
    this.props.data.refetch();
  };

  openAnalyticsModal = (featured) => {
    this.setState({
      analyticsModal: {
        isOpen: true,
        featuredListingId: featured.id,
        listTitle: featured.listing?.title || ''
      }
    });
  };

  closeAnalyticsModal = () => {
    this.setState({
      analyticsModal: { isOpen: false, featuredListingId: null, listTitle: '' }
    });
  };

  openCompetitionModal = (featured) => {
    this.setState({
      competitionModal: {
        isOpen: true,
        featuredListingId: featured.id,
        listTitle: featured.listing?.title || ''
      }
    });
  };

  closeCompetitionModal = () => {
    this.setState({
      competitionModal: { isOpen: false, featuredListingId: null, listTitle: '' }
    });
  };

  handleQuickTopUp = (amount) => {
    // Close competition modal and open topup modal with suggested amount
    const { competitionModal } = this.state;
    this.setState({
      competitionModal: { isOpen: false, featuredListingId: null, listTitle: '' },
      topUpModal: {
        isOpen: true,
        featuredListingId: competitionModal.featuredListingId,
        listTitle: competitionModal.listTitle,
        currentBid: 0, // Will be fetched by TopUpModal
        suggestedAmount: amount
      }
    });
  };

  getFilteredListings = () => {
    const { data } = this.props;
    const { filter } = this.state;
    
    if (!data?.getMyFeaturedListings) return [];
    
    const listings = data.getMyFeaturedListings;
    
    if (filter === 'all') return listings;
    return listings.filter(l => l.status === filter);
  };

  getSummary = () => {
    const { data } = this.props;
    const listings = data?.getMyFeaturedListings || [];
    
    const active = listings.filter(l => l.status === 'active');
    // Calculate average commission rate for display
    const avgCommission = active.length > 0 
      ? active.reduce((sum, l) => sum + parseFloat(l.commissionRate || 0), 0) / active.length 
      : 0;
    const expiringSoon = active.filter(l => {
      const daysLeft = moment(l.endDate).diff(moment(), 'days');
      return daysLeft <= 7 && daysLeft >= 0;
    });
    
    return {
      activeCount: active.length,
      avgCommission: avgCommission.toFixed(1),
      totalListings: listings.length,
      expiringSoon: expiringSoon.length
    };
  };

  getStatusClass = (status) => {
    switch (status) {
      case 'active': return s.statusActive;
      case 'expired': return s.statusExpired;
      case 'cancelled': return s.statusCancelled;
      case 'pending': return s.statusPending;
      default: return '';
    }
  };

  getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'expired': return 'Expirado';
      case 'cancelled': return 'Cancelado';
      case 'pending': return 'Pendiente';
      default: return status;
    }
  };

  getCoverPhotoUrl = (listing) => {
    if (!listing) return '/images/placeholder.webp';
    
    const { coverPhoto, listPhotos } = listing;
    
    if (listPhotos && listPhotos.length > 0) {
      // Find the photo matching coverPhoto ID, or use first photo
      let photoName = listPhotos[0].name;
      if (coverPhoto) {
        const matched = listPhotos.find(p => p.id === coverPhoto);
        if (matched) photoName = matched.name;
      }
      // WebP-only after migration
      const webpName = photoName.replace(/\.(jpe?g|png)$/i, '.webp');
      return `/images/upload/x_medium_${webpName}`;
    }
    
    return '/images/placeholder.webp';
  };

  render() {
    const { data } = this.props;
    const { filter, topUpModal, analyticsModal, competitionModal } = this.state;
    
    if (data?.loading) {
      return <div className={s.loading}>Cargando...</div>;
    }

    const listings = this.getFilteredListings();
    const summary = this.getSummary();

    return (
      <div className={s.container}>
        {/* Commission Modal */}
        <TopUpModal
          isOpen={topUpModal.isOpen}
          onClose={this.closeTopUpModal}
          onSuccess={this.closeTopUpModal}
          featuredListingId={topUpModal.featuredListingId}
          listId={topUpModal.listId}
          listTitle={topUpModal.listTitle}
          currentCommissionRate={topUpModal.currentCommissionRate}
        />
        
        {/* Analytics Modal */}
        <AnalyticsPanel
          isOpen={analyticsModal.isOpen}
          onClose={this.closeAnalyticsModal}
          featuredListingId={analyticsModal.featuredListingId}
          listTitle={analyticsModal.listTitle}
        />

        {/* Competition Modal */}
        <CompetitionPanel
          isOpen={competitionModal.isOpen}
          onClose={this.closeCompetitionModal}
          featuredListingId={competitionModal.featuredListingId}
          listTitle={competitionModal.listTitle}
          onTopUp={this.handleQuickTopUp}
        />

        {/* Header */}
        <div className={s.header}>
          <h1 className={s.title}>
            <Star size={24} />
            Mis Botes Destacados
          </h1>
          <Link to="/boats" className={s.newButton}>
            + Nuevo Destacado
          </Link>
        </div>

        {/* Summary Cards */}
        <div className={s.summaryGrid}>
          <div className={s.summaryCard}>
            <div className={cx(s.summaryIcon, s.iconBlue)}><BarChart2 size={24} /></div>
            <p className={s.summaryValue}>{summary.activeCount}</p>
            <p className={s.summaryLabel}>Activos</p>
          </div>
          <div className={s.summaryCard}>
            <div className={cx(s.summaryIcon, s.iconGreen)}><Percent size={24} /></div>
            <p className={s.summaryValue}>{summary.avgCommission}%</p>
            <p className={s.summaryLabel}>Comisión Promedio</p>
          </div>
          <div className={s.summaryCard}>
            <div className={cx(s.summaryIcon, s.iconPurple)}><Ship size={24} /></div>
            <p className={s.summaryValue}>{summary.totalListings}</p>
            <p className={s.summaryLabel}>Total Destacados</p>
          </div>
          <div className={s.summaryCard}>
            <div className={cx(s.summaryIcon, s.iconOrange)}><Clock size={24} /></div>
            <p className={s.summaryValue}>{summary.expiringSoon}</p>
            <p className={s.summaryLabel}>Vencen Pronto</p>
          </div>
        </div>

        {/* Filters */}
        <div className={s.filters}>
          {['all', 'active', 'expired', 'cancelled'].map(f => (
            <button
              key={f}
              className={cx(s.filterBtn, { [s.filterBtnActive]: filter === f })}
              onClick={() => this.setState({ filter: f })}
            >
              {f === 'all' ? 'Todos' : this.getStatusLabel(f)}
            </button>
          ))}
        </div>

        {/* Listings */}
        {listings.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}><Star size={48} /></div>
            <h3 className={s.emptyTitle}>
              {filter === 'all' ? 'No tienes botes destacados' : `No hay botes ${this.getStatusLabel(filter).toLowerCase()}s`}
            </h3>
            <p className={s.emptyText}>
              Destaca tu bote para aparecer en la página principal y recibir más reservas.
            </p>
            <Link to="/boats" className={s.newButton}>
              Destacar un Bote
            </Link>
          </div>
        ) : (
          <div className={s.listingsGrid}>
            {listings.map(featured => {
              const listing = featured.listing;
              const commissionRate = parseFloat(featured.commissionRate) || 0;
              const daysLeft = moment(featured.endDate).diff(moment(), 'days');
              
              return (
                <div key={featured.id} className={s.listingCard}>
                  {featured.tier === 'premium' && (
                    <div className={s.premiumBadge}>
                      <Crown size={14} />
                      <span>PREMIUM</span>
                    </div>
                  )}
                  <img
                    src={this.getCoverPhotoUrl(listing)}
                    alt={listing?.title}
                    className={s.listingImage}
                  />
                  <div className={s.listingInfo}>
                    <div className={s.listingHeader}>
                      <h3 className={s.listingTitle}>{listing?.title || 'Sin título'}</h3>
                      <span className={cx(s.statusBadge, this.getStatusClass(featured.status))}>
                        {this.getStatusLabel(featured.status)}
                      </span>
                    </div>
                    
                    <p className={s.listingMeta}>
                      {featured.status === 'active' && daysLeft >= 0 && (
                        <>Vence en {daysLeft} días • </>
                      )}
                      {featured.status === 'expired' && (
                        <>Expiró el {moment(featured.endDate).format('DD MMM YYYY')} • </>
                      )}
                      {listing?.city || 'Sin ubicación'}
                    </p>

                    {featured.status === 'active' && (
                      <div className={s.bidSection}>
                        <div className={s.bidInfo}>
                          <span className={s.bidLabel}><Percent size={14} /> Comisión:</span>
                          <span className={s.bidValue}>{commissionRate.toFixed(1)}%</span>
                        </div>
                        <div className={s.bidProgress}>
                          <div className={s.progressBar}>
                            <div className={s.progressFill} style={{ width: `${Math.min(commissionRate * 2.5, 100)}%`, backgroundColor: commissionRate > 0 ? '#10B981' : '#E5E7EB' }} />
                          </div>
                          <span className={s.progressText}>
                            {featured.position ? (
                              <>Posición <strong>#{featured.position}</strong> de {featured.totalCompetitors}</>
                            ) : (
                              commissionRate > 0 ? 'Posicionado' : 'Sin comisión'
                            )}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className={s.listingActions}>
                      {featured.status === 'active' && (
                        <>
                          <button 
                            className={cx(s.actionBtn, s.actionBtnAnalytics)}
                            onClick={() => this.openAnalyticsModal(featured)}
                          >
                            <BarChart2 size={16} />
                          </button>
                          <button 
                            className={cx(s.actionBtn, s.actionBtnCompetition)}
                            onClick={() => this.openCompetitionModal(featured)}
                          >
                            <Trophy size={16} />
                          </button>
                          <Link to={`/boats/${featured.listId}`} className={s.actionBtn}>
                            <Eye size={16} />
                          </Link>
                          <button 
                            className={cx(s.actionBtn, s.actionBtnPrimary)}
                            onClick={() => this.openTopUpModal(featured)}
                          >
                            <Settings size={16} />
                          </button>
                        </>
                      )}
                      {featured.status === 'expired' && (
                        <Link to="/boats" className={cx(s.actionBtn, s.actionBtnPrimary)}>
                          <RefreshCw size={14} /> Reactivar
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}

const mapState = (state) => ({});

export default compose(
  withStyles(s),
  withApollo,
  connect(mapState),
  graphql(GET_FEATURED_DASHBOARD, {
    options: {
      fetchPolicy: 'network-only',
      ssr: false
    }
  }),
  injectIntl
)(FeaturedDashboard);
